// src/utils/AnalyticsCalculator.js

/**
 * Функция для расчёта динамики доходов и расходов по годам.
 * @param {Object} incomeObj - Объект доходов (salary, sideIncome, interests, subsidies, revenue, gifts, otherIncome, incomegrowth).
 * @param {Object} expensesObj - Объект расходов (meals, rental, utility, transportation, clothing, education, telecom, entertainment, medical, reserve, outcomegrowth).
 * @param {number} periodYears - Период расчёта в годах.
 * @returns {Object} - Массивы значений доходов и расходов за каждый год.
 */
export const calculateIncomeExpenseGrowth = (incomeObj, expensesObj, periodYears) => {
  const initialIncome =
    (incomeObj.salary || 0) +
    (incomeObj.sideIncome || 0) +
    (incomeObj.interests || 0) +
    (incomeObj.subsidies || 0) +
    (incomeObj.revenue || 0) +
    (incomeObj.gifts || 0) +
    (incomeObj.otherIncome || 0);
  const incomeGrowthRate = incomeObj.incomegrowth || 0;
  
  const initialExpense =
    (expensesObj.meals || 0) +
    (expensesObj.rental || 0) +
    (expensesObj.utility || 0) +
    (expensesObj.transportation || 0) +
    (expensesObj.clothing || 0) +
    (expensesObj.education || 0) +
    (expensesObj.telecom || 0) +
    (expensesObj.entertainment || 0) +
    (expensesObj.medical || 0) +
    (expensesObj.reserve || 0);
  const expenseGrowthRate = expensesObj.outcomegrowth || 0;
  
  const incomeValues = [];
  const expenseValues = [];
  let currentIncome = initialIncome;
  let currentExpense = initialExpense;
  
  for (let i = 0; i <= periodYears; i++) {
    incomeValues.push(currentIncome);
    expenseValues.push(currentExpense);
    currentIncome *= 1 + incomeGrowthRate / 100;
    currentExpense *= 1 + expenseGrowthRate / 100;
  }
  return { incomeValues, expenseValues };
};

/**
 * Функция для расчёта годовых затрат по аренде и ипотеке без учёта роста.
 * @param {Object} rentObj - Объект с данными об аренде (rent).
 * @param {Object} flatObj - Объект с данными об ипотеке (monthly).
 * @param {number} periodYears - Период расчёта в годах.
 * @returns {Object} - Массивы годовых затрат для аренды и ипотеки без роста.
 */
export const calculateRentMortgageNoGrowth = (rentObj, flatObj, periodYears) => {
  const monthlyRent = rentObj.rent || 0;
  const monthlyMortgage = flatObj.monthly || 0;
  const rentNoGrowth = [];
  const mortgageNoGrowth = [];
  for (let i = 0; i <= periodYears; i++) {
    rentNoGrowth.push(monthlyRent * 12);
    mortgageNoGrowth.push(monthlyMortgage * 12);
  }
  return { rentNoGrowth, mortgageNoGrowth };
};

/**
 * Функция для расчёта динамики годовых затрат по аренде с учетом роста.
 * Для ипотеки, как правило, рост фиксированный, поэтому mortgageGrowthRate = 0.
 * @param {Object} rentObj - Объект с данными об аренде (rent, rentgrowth).
 * @param {Object} flatObj - Объект с данными об ипотеке (monthly).
 * @param {number} periodYears - Период расчёта в годах.
 * @returns {Object} - Массивы годовых затрат для аренды и ипотеки с учетом роста.
 */
export const calculateRentMortgageGrowth = (rentObj, flatObj, periodYears) => {
  const monthlyRent = rentObj.rent || 0;
  const rentGrowthRate = rentObj.rentgrowth || 0;
  const monthlyMortgage = flatObj.monthly || 0;
  const mortgageGrowthRate = 0;
  
  const rentGrowthValues = [];
  const mortgageGrowthValues = [];
  let yearlyRent = monthlyRent * 12;
  let yearlyMortgage = monthlyMortgage * 12;
  for (let i = 0; i <= periodYears; i++) {
    rentGrowthValues.push(yearlyRent);
    mortgageGrowthValues.push(yearlyMortgage);
    yearlyRent *= 1 + rentGrowthRate / 100;
    yearlyMortgage *= 1 + mortgageGrowthRate / 100;
  }
  return { rentGrowthValues, mortgageGrowthValues };
};

/**
 * Функция для расчёта итоговых сумм по разделам доходов и расходов.
 * @param {Object} data - Полный объект данных, содержащий поля income и expenses.
 * @returns {Object} - Итоговые суммы totalIncome и totalExpenses.
 */
export const calculateTotals = (data) => {
  const incomeObj = data.income || {};
  const expensesObj = data.expenses || {};
  
  const totalIncome =
    (incomeObj.salary || 0) +
    (incomeObj.sideIncome || 0) +
    (incomeObj.interests || 0) +
    (incomeObj.subsidies || 0) +
    (incomeObj.revenue || 0) +
    (incomeObj.gifts || 0) +
    (incomeObj.otherIncome || 0);
  
  const totalExpenses =
    (expensesObj.meals || 0) +
    (expensesObj.rental || 0) +
    (expensesObj.utility || 0) +
    (expensesObj.transportation || 0) +
    (expensesObj.clothing || 0) +
    (expensesObj.education || 0) +
    (expensesObj.telecom || 0) +
    (expensesObj.entertainment || 0) +
    (expensesObj.medical || 0) +
    (expensesObj.reserve || 0);
    
  return { totalIncome, totalExpenses };
};
